'use client';

// Inspired by react-hot-toast library
import * as React from 'react';

import type { ToastActionElement, ToastProps } from '@/components/ui/toast';
import { isStaleBundleValue, reloadToLatestVersion } from '@/lib/utils/stale-bundle-patterns';

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 5000;
/** Default auto-dismiss for a plain toast (no action). */
const TOAST_AUTO_DISMISS_DELAY = 4000;
/** Auto-dismiss when the toast carries an action button (give time to click). */
const TOAST_ACTION_DISMISS_DELAY = 7000;
/**
 * Pass as `duration` to keep a toast alive until explicitly dismissed or updated.
 * Used by transient-loading patterns (e.g. two-stage welcome-message toast).
 * All other callers that don't pass `duration` use TOAST_AUTO_DISMISS_DELAY — no change.
 */
export const TOAST_DURATION_PERSISTENT = 120_000; // 2 min hard-cap (safety net)

/**
 * Toast variant — drives the glass tint + leading icon in the Toaster.
 * 'default' | 'destructive' kept for back-compat; sonner-style variants added.
 */
export type ToastVariant =
  | 'default'
  | 'destructive'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'
  | 'loading';

/**
 * Object-form action descriptor (sonner-style). The Toaster builds a
 * <ToastAction> from this. `action` may ALSO be a pre-built ReactElement
 * (legacy callers) — the Toaster handles both shapes.
 */
export interface ToastActionObject {
  label: string;
  onClick: () => void;
}

type ToasterToast = Omit<ToastProps, 'title'> & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * Either a pre-built ToastAction element (legacy) or a {label,onClick}
   * descriptor (sonner-style) — the Toaster renders both.
   */
  action?: ToastActionElement | ToastActionObject;
  /** Leading variant icon override (defaults derived from variant in the Toaster). */
  icon?: React.ReactNode;
  /** Optional click handler - makes toast clickable for navigation/scroll actions */
  onClick?: () => void;
  /**
   * Auto-dismiss delay in ms. Defaults to TOAST_AUTO_DISMISS_DELAY (4000ms).
   * If `action` is present and no explicit duration is passed → 7000ms.
   * Pass TOAST_DURATION_PERSISTENT (120_000) for loading-state toasts that
   * should stay alive until explicitly dismissed via the returned `dismiss()`.
   */
  duration?: number;
};

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType['ADD_TOAST'];
      toast: ToasterToast;
    }
  | {
      type: ActionType['UPDATE_TOAST'];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType['DISMISS_TOAST'];
      toastId?: ToasterToast['id'];
    }
  | {
      type: ActionType['REMOVE_TOAST'];
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId,
    });
  }, TOAST_REMOVE_DELAY);

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      };

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t)),
      };

    case 'DISMISS_TOAST': {
      const { toastId } = action;

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

// ─── Stale-bundle refresh prompt (post-deploy "Server Action not found") ─────────────
// ROOT-CAUSE: ~150 call sites toast `error.message` verbatim, so after every deploy staff saw
// raw Next.js text ('Server Action "4018…" was not found on the server. Read more: …').
// CLASS: raw-framework-error-text-in-user-toast — gated HERE, at the sole toast leaf, so no
// call site can leak it; each stale toast collapses into ONE bilingual refresh prompt.

/** Fixed id: at most one refresh prompt exists at a time. */
export const STALE_BUNDLE_TOAST_ID = 'stale-bundle-refresh';

/**
 * Single source of copy (inline "EN / RO" — the onboarding page precedent).
 * Kept short on purpose: the Toaster sets the action beside the text, so at 1920px the text
 * column is 128px. Measured live 2026-09-16: the longer first draft wrapped to 9 lines / 224px
 * and split the Romanian title mid-phrase; this copy renders 2+3 lines / ~145px.
 */
export const STALE_BUNDLE_PROMPT_COPY = {
  title: 'New version / Versiune nouă',
  description: 'Refresh to continue. / Reîncarcă pentru a continua.',
  descriptionDraftKept: 'Refresh — your draft is kept. / Reîncarcă — ciorna e păstrată.',
  actionLabel: 'Refresh / Reîncarcă',
} as const;

let staleBundlePromptDraftKept = false;
let staleBundlePromptCapTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Show (or dedupe into) the single refresh prompt. While one is open a repeat call is a
 * no-op, except that `draftKept: true` upgrades the open prompt's description (never the
 * reverse — a later generic signal must not hide that the draft was saved).
 */
export function showStaleBundlePrompt(options?: { draftKept?: boolean }): void {
  const existing = memoryState.toasts.find((t) => t.id === STALE_BUNDLE_TOAST_ID);
  const isOpen = existing !== undefined && existing.open !== false;
  const draftKept = options?.draftKept === true || (isOpen && staleBundlePromptDraftKept);
  if (isOpen && draftKept === staleBundlePromptDraftKept) return;
  staleBundlePromptDraftKept = draftKept;

  const content = {
    title: STALE_BUNDLE_PROMPT_COPY.title,
    description: draftKept
      ? STALE_BUNDLE_PROMPT_COPY.descriptionDraftKept
      : STALE_BUNDLE_PROMPT_COPY.description,
    variant: 'info' as const,
    duration: TOAST_DURATION_PERSISTENT,
    action: {
      label: STALE_BUNDLE_PROMPT_COPY.actionLabel,
      onClick: reloadToLatestVersion,
    },
    open: true,
  };

  if (existing) {
    // A dismissed prompt may still be queued for removal — keep it and re-open in place.
    const pendingRemoval = toastTimeouts.get(STALE_BUNDLE_TOAST_ID);
    if (pendingRemoval) {
      clearTimeout(pendingRemoval);
      toastTimeouts.delete(STALE_BUNDLE_TOAST_ID);
    }
    dispatch({ type: 'UPDATE_TOAST', toast: { ...content, id: STALE_BUNDLE_TOAST_ID } });
  } else {
    dispatch({
      type: 'ADD_TOAST',
      toast: {
        ...content,
        id: STALE_BUNDLE_TOAST_ID,
        onOpenChange: (open) => {
          if (!open) dispatch({ type: 'DISMISS_TOAST', toastId: STALE_BUNDLE_TOAST_ID });
        },
      },
    });
  }

  if (!isOpen) {
    // Persistent-toast convention: alive until dismissed, with the file's hard safety cap.
    if (staleBundlePromptCapTimer) clearTimeout(staleBundlePromptCapTimer);
    staleBundlePromptCapTimer = setTimeout(() => {
      staleBundlePromptCapTimer = null;
      dispatch({ type: 'DISMISS_TOAST', toastId: STALE_BUNDLE_TOAST_ID });
    }, TOAST_DURATION_PERSISTENT);
  }
}

function carriesStaleBundleText(props: { title?: unknown; description?: unknown }): boolean {
  return isStaleBundleValue(props.title) || isStaleBundleValue(props.description);
}

/**
 * Input payload for createToast(). Override Radix's narrow `title`/`description`
 * (typed as `string`) with `React.ReactNode`, and allow an optional `id` so
 * promise()/loading-update flows can target an existing toast.
 */
type Toast = Omit<ToasterToast, 'id' | 'title' | 'description'> & {
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
};

/** Return shape of every `toast()` / `toast.success()` / … call. */
export interface ToastHandle {
  id: string;
  dismiss: () => void;
  update: (props: Toast) => void;
}

/**
 * Options accepted by the positional sonner-style call forms
 * (`toast.success(message, opts)` etc.). Mirrors a subset of ToasterToast.
 */
export interface ToastOptions {
  description?: React.ReactNode;
  duration?: number;
  action?: ToastActionObject;
  /** Reuse an existing toast id (used by promise()/loading update flows). */
  id?: string;
  /** Click handler — makes the toast clickable. */
  onClick?: () => void;
  icon?: React.ReactNode;
}

/**
 * Core dispatch. Computes the effective auto-dismiss delay:
 *   - explicit `duration` wins
 *   - else `action` present → 7000ms
 *   - else 4000ms
 * `loading` variant is persistent (TOAST_DURATION_PERSISTENT) unless overridden.
 */
// ROOT-CAUSE: a caller `duration: Infinity` reached setTimeout; WebIDL converts a non-finite or
// >2^31-1 delay to 0 (Node: 1ms), so the "persistent" stale-bundle prompt dismissed itself instantly.
// CLASS: non-finite-timer-delay-dismisses-instantly — every caller-supplied delay passes this clamp.
const MAX_TIMER_DELAY_MS = 2_147_483_647;
function toTimerDelay(ms: number): number {
  return !Number.isFinite(ms) || ms > MAX_TIMER_DELAY_MS ? TOAST_DURATION_PERSISTENT : ms;
}

function createToast({ duration, ...rest }: Toast): ToastHandle {
  const props: Toast = {
    ...rest,
    duration: duration === undefined ? undefined : toTimerDelay(duration),
  };
  const id = props.id ?? genId();

  const dismiss = () => dispatch({ type: 'DISMISS_TOAST', toastId: id });
  const update = (next: Toast) => {
    // promise()/loading flows land their error text through update — same leaf gate.
    if (carriesStaleBundleText(next)) {
      dismiss();
      showStaleBundlePrompt();
      return;
    }
    const clamped =
      next.duration === undefined ? next : { ...next, duration: toTimerDelay(next.duration) };
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...clamped, id },
    });
  };

  if (carriesStaleBundleText(props)) {
    // Never render the raw framework text. The returned handle targets THIS call's id,
    // so a caller dismissing "its" error toast cannot close the shared refresh prompt.
    if (props.id) dismiss();
    showStaleBundlePrompt();
    return { id, dismiss, update };
  }

  dispatch({
    type: 'ADD_TOAST',
    toast: {
      ...props,
      id,
      open: true,
      onOpenChange: (open) => {
        if (!open) dismiss();
      },
    },
  });

  // Auto-dismiss after delay — don't rely on Radix's internal timer
  // which can silently fail with controlled `open` prop.
  const dismissDelay =
    props.duration ??
    (props.variant === 'loading'
      ? TOAST_DURATION_PERSISTENT
      : props.action
        ? TOAST_ACTION_DISMISS_DELAY
        : TOAST_AUTO_DISMISS_DELAY);

  if (dismissDelay !== TOAST_DURATION_PERSISTENT) {
    setTimeout(() => {
      dismiss();
    }, dismissDelay);
  } else {
    // Persistent (loading) toasts still get a hard safety cap so a never-resolved
    // promise can't leave a spinner on screen forever.
    setTimeout(() => {
      dismiss();
    }, TOAST_DURATION_PERSISTENT);
  }

  return { id, dismiss, update };
}

/**
 * Build a ToasterToast payload from the positional (message, opts) call form.
 */
function fromPositional(
  message: React.ReactNode,
  opts: ToastOptions | undefined,
  variant: ToastVariant
): Toast {
  return {
    title: message,
    description: opts?.description,
    duration: opts?.duration,
    action: opts?.action,
    id: opts?.id,
    onClick: opts?.onClick,
    icon: opts?.icon,
    variant,
  };
}

/**
 * Callable signature of the exported `toast`: BOTH the legacy object form
 * `toast({ title, ... })` and the sonner positional form `toast(message, opts)`.
 */
interface ToastFn {
  (props: Toast): ToastHandle;
  (message: React.ReactNode, opts?: ToastOptions): ToastHandle;
}

const toastBase: ToastFn = ((arg: Toast | React.ReactNode, opts?: ToastOptions): ToastHandle => {
  // Object form: a plain options object (has title/description/variant/etc.).
  if (
    arg !== null &&
    typeof arg === 'object' &&
    !React.isValidElement(arg) &&
    ('title' in (arg as object) ||
      'description' in (arg as object) ||
      'variant' in (arg as object) ||
      'action' in (arg as object) ||
      'duration' in (arg as object) ||
      'onClick' in (arg as object))
  ) {
    return createToast(arg as Toast);
  }
  // Positional form: bare message + opts → default variant.
  return createToast(fromPositional(arg as React.ReactNode, opts, 'default'));
}) as ToastFn;

/** Sonner-style variant helpers + dismiss + promise, attached to `toast`. */
interface ToastApi extends ToastFn {
  success: (message: React.ReactNode, opts?: ToastOptions) => ToastHandle;
  error: (message: React.ReactNode, opts?: ToastOptions) => ToastHandle;
  info: (message: React.ReactNode, opts?: ToastOptions) => ToastHandle;
  warning: (message: React.ReactNode, opts?: ToastOptions) => ToastHandle;
  loading: (message: React.ReactNode, opts?: ToastOptions) => ToastHandle;
  dismiss: (id?: string) => void;
  promise: <T>(
    promise: Promise<T> | (() => Promise<T>),
    msgs: {
      loading: React.ReactNode;
      success: React.ReactNode | ((data: T) => React.ReactNode);
      error: React.ReactNode | ((err: unknown) => React.ReactNode);
    },
    opts?: ToastOptions
  ) => Promise<T>;
}

const toast = toastBase as ToastApi;

toast.success = (message, opts) => createToast(fromPositional(message, opts, 'success'));
toast.error = (message, opts) => createToast(fromPositional(message, opts, 'error'));
toast.info = (message, opts) => createToast(fromPositional(message, opts, 'info'));
toast.warning = (message, opts) => createToast(fromPositional(message, opts, 'warning'));
toast.loading = (message, opts) => createToast(fromPositional(message, opts, 'loading'));

toast.dismiss = (id?: string) => dispatch({ type: 'DISMISS_TOAST', toastId: id });

toast.promise = async function promise<T>(
  promiseOrFn: Promise<T> | (() => Promise<T>),
  msgs: {
    loading: React.ReactNode;
    success: React.ReactNode | ((data: T) => React.ReactNode);
    error: React.ReactNode | ((err: unknown) => React.ReactNode);
  },
  opts?: ToastOptions
): Promise<T> {
  const handle = createToast(fromPositional(msgs.loading, opts, 'loading'));
  const p = typeof promiseOrFn === 'function' ? promiseOrFn() : promiseOrFn;
  try {
    const data = await p;
    const successMsg =
      typeof msgs.success === 'function'
        ? (msgs.success as (d: T) => React.ReactNode)(data)
        : msgs.success;
    handle.update({
      id: handle.id,
      title: successMsg,
      variant: 'success',
      duration: opts?.duration ?? TOAST_AUTO_DISMISS_DELAY,
      open: true,
    });
    // Re-arm auto-dismiss for the now-resolved toast (clamped — see toTimerDelay).
    setTimeout(handle.dismiss, toTimerDelay(opts?.duration ?? TOAST_AUTO_DISMISS_DELAY));
    return data;
  } catch (err) {
    const errorMsg =
      typeof msgs.error === 'function'
        ? (msgs.error as (e: unknown) => React.ReactNode)(err)
        : msgs.error;
    handle.update({
      id: handle.id,
      title: errorMsg,
      variant: 'error',
      duration: opts?.duration ?? TOAST_AUTO_DISMISS_DELAY,
      open: true,
    });
    setTimeout(handle.dismiss, toTimerDelay(opts?.duration ?? TOAST_AUTO_DISMISS_DELAY));
    throw err;
  }
};

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toastId }),
  };
}

export { useToast, toast };
